/**
 * A DG acompanha a TE.
 *
 * As linhas da DG são as receptoras da TE: uma por transferência aprovada. Mas
 * a DG guardava uma CÓPIA de quando a aba abriu, e essa cópia nunca mais era
 * conferida. Em 30/10/2026, na Rio Minas:
 *
 *   - 2026-0119: TE com 47 aprovadas, DG30 com 15 linhas;
 *   - 2026-0105: 8 linhas da DG apontando para transferências que a TE já
 *     tinha desfeito, com a receptora antiga;
 *   - 2026-0102: 3 receptoras trocadas na TE e mantidas velhas na DG;
 *   - dezenas de linhas sem `te_transfer_id`, gravadas no mesmo "Salvar" que
 *     criou a transferência (a aba Prenhez montou a lista antes de a TE ter id).
 *
 * E corrigir na DG não dava: a linha não tem como ser excluída lá.
 *
 * A regra, linha a linha:
 *
 *   - transferência aprovada com linha na DG: a linha fica, com o resultado,
 *     e a receptora, o embrião e a data prevista passam a ser os da TE;
 *   - linha sem vínculo: casa pela receptora (o brinco), e passa a ter vínculo;
 *   - transferência aprovada sem linha: entra uma linha em branco;
 *   - linha cuja transferência saiu da TE: SAI, se ainda não tem diagnóstico.
 *     Com diagnóstico lançado, fica, marcada `_foraDaTE` — apagar resultado
 *     por iniciativa da tela não é coisa que ela decida sozinha. A tela
 *     oferece a lixeira para essa linha.
 *
 * A ordem da DG é preservada (ela pode ter sido arrastada); o que entra novo
 * vai para o fim, na ordem da TE.
 */

const brinco = (x) => String(x || '').trim().toUpperCase();

/** A linha já tem diagnóstico, ou algo que alguém escreveu nela? */
export const temLancamento = (c = {}) =>
  !!(c.result || (c.observations || '').trim() || c.sexing?.performed);

const linhaEmBranco = (t, { teDate, previsto, prefixo }, i) => ({
  id: `${prefixo}-te-${t.id || i}`,
  transfer_id: t.id,
  recipient_id: t.recipient_id || null,
  recipient_identifier: t.recipient_identifier || null,
  embryo_number: t.embryo_number ?? null,
  te_date: teDate || null,
  expected_date: previsto ? (t[previsto] || null) : null,
  result: null, viability: null, heartbeat: null, size_mm: null,
  empty_cause: null, inconclusive_reason: null, reschedule_date: null,
  observations: '',
});

/**
 * Reconcilia as linhas de uma DG com as transferências aprovadas da TE.
 *
 * @param checks       linhas da DG, na ordem da tela
 * @param transfers    transferências APROVADAS da TE, como vêm do banco
 * @param opcoes.teDate    data da TE (vai para as linhas)
 * @param opcoes.previsto  coluna da transferência com a data prevista
 *                         ('expected_dg30'), ou null para não mexer na data
 * @param opcoes.prefixo   prefixo do id das linhas novas ('dg30' | 'dg60')
 * @returns { checks, mudou: { atualizadas, entraram, sairam, foraDaTE } }
 */
export const dgSincronizadaComATE = (checks = [], transfers = [], opcoes = {}) => {
  const { teDate = null, previsto = null, prefixo = 'dg' } = opcoes;
  const porId = new Map(transfers.filter(t => t.id).map(t => [t.id, t]));
  const usadas = new Set();

  // 1. Quem já tem vínculo válido.
  const vinculo = checks.map(c => {
    const t = c.transfer_id && porId.get(c.transfer_id);
    if (t && !usadas.has(t.id)) { usadas.add(t.id); return t; }
    return null;
  });

  // 2. Sem vínculo (ou com vínculo para transferência que saiu): pelo brinco.
  checks.forEach((c, i) => {
    if (vinculo[i] || !brinco(c.recipient_identifier)) return;
    const t = transfers.find(x => x.id && !usadas.has(x.id)
      && brinco(x.recipient_identifier) === brinco(c.recipient_identifier));
    if (t) { usadas.add(t.id); vinculo[i] = t; }
  });

  const mudou = { atualizadas: 0, entraram: 0, sairam: 0, foraDaTE: 0 };
  const ficam = [];

  checks.forEach((c, i) => {
    const t = vinculo[i];
    if (!t) {
      if (temLancamento(c)) {
        mudou.foraDaTE += 1;
        ficam.push(c._foraDaTE ? c : { ...c, _foraDaTE: true });
      } else {
        mudou.sairam += 1;
      }
      return;
    }
    const daTE = {
      transfer_id: t.id,
      recipient_id: t.recipient_id || null,
      recipient_identifier: t.recipient_identifier || null,
      embryo_number: t.embryo_number ?? null,
      ...(teDate ? { te_date: teDate } : {}),
      ...(previsto && t[previsto] ? { expected_date: t[previsto] } : {}),
    };
    const diferente = Object.entries(daTE).some(([k, v]) => (c[k] ?? null) !== v) || c._foraDaTE;
    if (!diferente) { ficam.push(c); return; }
    mudou.atualizadas += 1;
    const { _foraDaTE, ...resto } = c;
    ficam.push({ ...resto, ...daTE });
  });

  const novas = transfers.filter(t => t.id && !usadas.has(t.id))
    .map((t, i) => linhaEmBranco(t, { teDate, previsto, prefixo }, i));
  mudou.entraram = novas.length;

  return { checks: [...ficam, ...novas], mudou };
};

/** Houve alguma mudança? */
export const algoMudou = (m) => !!(m && (m.atualizadas || m.entraram || m.sairam));
